import type { RaceSnapshot } from '../../shared/race';
import { nearestPace, positionAt, type ChaseCar, type RaceVisuals, type VisualDriver } from '../../shared/race-visuals';

export type StudioView = 'story' | 'chase' | 'terrain';
export type StudioMotionMode = 'live' | 'replay';
/** Plot hits select a driver; the timeline is the only time-seeking control. */
export function reviewTimeForStudioHit(_view: StudioView, _hit: { atMs?: number } | null): undefined {
  return undefined;
}
export interface Point { x: number; y: number }
export interface PlotPoint extends Point { atMs: number; lap?: number; breakBefore?: boolean; hold?: boolean; partial?: boolean }
export interface DriverGeometry { number: number; view: StudioView; points: PlotPoint[]; feet: Point[]; node: Point | null; label: Point; headAtMs?: number }
export interface StudioTimeDomain { fromMs: number; toMs: number }
export interface StudioLayout { width: number; height: number; view: StudioView; flat: boolean; yaw: number; pitch: number; atMs: number; paceScale: number; gapScale?: number; timeDomain?: StudioTimeDomain; stableTerrain?: boolean; visibleUntilMs?: number }
export const clamp = (value: number, low: number, high: number) => Math.max(low, Math.min(high, value));
export const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
export const timeX = (time: number, model: RaceVisuals, width: number, domain?: StudioTimeDomain) => 36 + clamp((time - (domain?.fromMs ?? 0)) / Math.max(1, (domain?.toMs ?? model.endMs) - (domain?.fromMs ?? 0)), 0, 1) * Math.max(1, width - 91);
export const rankY = (rank: number, model: RaceVisuals, height: number) => 39 + (rank - 1) / Math.max(1, model.drivers.length - 1) * Math.max(1, height - 94);
export const paceHigh = (model: RaceVisuals) => Math.max(.5, Math.ceil(Math.max(0, ...model.drivers.flatMap(driver => driver.pace.map(lap => lap.delta))) * 2) / 2);
/** Keep the gap ruler from contracting whenever a car crosses the timing line. */
export const gapHigh = (cars: readonly ChaseCar[], previous = 0) => Math.max(5, previous, Math.ceil(Math.max(0, ...cars.map(car => car.seconds)) / 5) * 5);

/** Put the elevation ruler beside the observed peak, not an imaginary peak on the back row. */
export function terrainPaceAxisRow(model: RaceVisuals): number {
  let row = 0, peak = -1;
  model.drivers.forEach((driver, index) => driver.pace.forEach(lap => { if (lap.delta > peak) { peak = lap.delta; row = index; } }));
  return row;
}
interface TerrainFrame { model: RaceVisuals; depth: number; elevation: number; ceiling: number }
const terrainFrames = new WeakMap<StudioLayout, TerrainFrame>();
function terrainFrame(model: RaceVisuals, layout: StudioLayout): TerrainFrame {
  const cached = terrainFrames.get(layout);
  if (cached?.model === model) return cached;
  const depth = .44 + layout.pitch * .22, elevation = .45 - layout.pitch * .10;
  const rows = Math.max(1, model.drivers.length - 1);
  // Live geometry reserves the complete elevation range on every lane so a new
  // peak cannot move historical vertices. Finished sessions can fit observations.
  let ceiling = layout.stableTerrain ? depth + elevation : depth;
  if (!layout.stableTerrain) {
    model.drivers.forEach((driver, row) => driver.pace.forEach(lap => { ceiling = Math.max(ceiling, row / rows * depth + lap.delta / layout.paceScale * elevation); }));
    ceiling = Math.max(ceiling, terrainPaceAxisRow(model) / rows * depth + elevation);
  }
  const frame = { model, depth, elevation, ceiling }; terrainFrames.set(layout, frame); return frame;
}
export function terrainPoint(lap: number, row: number, value: number, model: RaceVisuals, layout: StudioLayout): Point {
  const { width, height, flat, yaw } = layout;
  const depth = row / Math.max(1, model.drivers.length - 1);
  if (flat) return { x: 38 + (lap - 1) / Math.max(1, model.lastLap - 1) * Math.max(1, width - 90), y: 39 + depth * Math.max(1, height - 94) };
  const shift = Math.min(100, width * .17) * (1 + yaw);
  const frame = terrainFrame(model, layout), top = 31, bottom = height - 48;
  return { x: 38 + (lap - 1) / Math.max(1, model.lastLap - 1) * Math.max(1, width - 84 - shift) + depth * shift,
    y: bottom - (depth * frame.depth + value / layout.paceScale * frame.elevation) / frame.ceiling * (bottom - top) };
}
export function driverGeometry(driver: VisualDriver, index: number, model: RaceVisuals, snapshot: RaceSnapshot, layout: StudioLayout, cars: ReadonlyMap<number, ChaseCar> = new Map()): DriverGeometry {
  const { view, width, height, atMs } = layout;
  const visibleUntil = Math.max(0, Math.min(model.evidenceEndMs ?? model.endMs, layout.visibleUntilMs ?? model.evidenceEndMs ?? model.endMs));
  let points: PlotPoint[] = [], feet: Point[] = [], node: Point | null = null, label: Point = { x: width - 47, y: rankY(index + 1, model, height) };
  if (view === 'story') {
    const ranks = driver.ranks.filter(point => point.atMs <= visibleUntil);
    const last = ranks.at(-1);
    if ((layout.visibleUntilMs !== undefined || model.evidenceEndMs !== undefined) && last && last.atMs < visibleUntil) {
      const gap = snapshot.coverage?.gaps.filter(item => item.fromMs >= last.atMs && item.fromMs < visibleUntil).sort((a, b) => a.fromMs - b.fromMs)[0];
      const toMs = Math.min(visibleUntil, gap?.fromMs ?? visibleUntil);
      if (toMs > last.atMs) ranks.push({ ...last, atMs: toMs, breakBefore: false });
    }
    points = ranks.flatMap((point, rankIndex) => {
      const previous = ranks[rankIndex - 1];
      // raceVisuals appends this mutable endcap to hold the latest observed rank.
      // It is not another historical observation when the live watermark moves.
      const holdBoundary = visibleUntil;
      const hold = rankIndex === driver.ranks.length - 1 && point.atMs === holdBoundary && previous?.position === point.position && !point.breakBefore;
      const next = { x: timeX(point.atMs, model, width, layout.timeDomain), y: rankY(point.position, model, height), ...point, ...(hold ? { hold: true } : {}) };
      // A rank changes at its observed timestamp, never along an invented diagonal.
      return previous && previous.position !== point.position && !point.breakBefore
        ? [{ ...next, y: rankY(previous.position, model, height) }, next] : [next];
    });
    const rank = positionAt(driver, atMs, snapshot.coverage);
    node = rank === null ? null : { x: timeX(atMs, model, width, layout.timeDomain), y: rankY(rank, model, height) };
    if (points.length) label = { x: points.at(-1)!.x + 7, y: points.at(-1)!.y };
  } else if (view === 'chase') {
    const car = cars.get(driver.number);
    if (car) {
      const y = rankY(car.position, model, height), x = 58 + car.seconds / (layout.gapScale ?? 5) * Math.max(1, width - 155);
      // These two points are a current gap, not time-stamped observations.
      points = [{ x: 58, y, atMs: 0 }, { x, y, atMs: 0 }]; node = { x, y }; label = { x: 3, y };
    }
  } else {
    const pace = driver.pace.filter(item => item.atMs <= visibleUntil);
    points = pace.map((item, pointIndex, all) => ({ ...terrainPoint(item.lap, index, item.delta, model, layout), atMs: item.atMs, lap: item.lap, breakBefore: pointIndex > 0 && item.lap !== all[pointIndex - 1].lap + 1 }));
    feet = pace.map(item => terrainPoint(item.lap, index, 0, model, layout));
    const current = nearestPace(driver, atMs);
    if (current) node = terrainPoint(current.lap, index, current.delta, model, layout);
    label = layout.flat ? { x: 3, y: terrainPoint(1, index, 0, model, layout).y } : { x: terrainPoint(model.lastLap, index, 0, model, layout).x + 7, y: terrainPoint(model.lastLap, index, 0, model, layout).y };
  }
  return { number: driver.number, view, points, feet: feet.length ? feet : points.map(({ x, y }) => ({ x, y })), node, label };
}
export function pathData(points: PlotPoint[]): string {
  return points.map((point, index) => `${!index || point.breakBefore ? 'M' : 'L'}${point.x.toFixed(2)},${point.y.toFixed(2)}`).join(' ');
}
function atFraction(points: Point[], fraction: number, fallback: Point): Point {
  if (!points.length) return fallback;
  const at = fraction * (points.length - 1), low = Math.floor(at), high = Math.ceil(at);
  return { x: lerp(points[low].x, points[high].x, at - low), y: lerp(points[low].y, points[high].y, at - low) };
}
const samePoint = (a: Point | null, b: Point | null) => a === b || Boolean(a && b && a.x === b.x && a.y === b.y);
const samePlotPoints = (a: PlotPoint[], b: PlotPoint[]) => a === b || a.length === b.length && a.every((point, index) => {
  const other = b[index]; return samePoint(point, other) && point.atMs === other.atMs && point.lap === other.lap && point.breakBefore === other.breakBefore && point.hold === other.hold && point.partial === other.partial;
});
const sameFeet = (a: Point[], b: Point[]) => a === b || a.length === b.length && a.every((point, index) => samePoint(point, b[index]));
export function sameDriverGeometry(a: DriverGeometry, b: DriverGeometry): boolean {
  return a === b || a.number === b.number && a.view === b.view && a.headAtMs === b.headAtMs && samePoint(a.node, b.node) && samePoint(a.label, b.label)
    && samePlotPoints(a.points, b.points) && sameFeet(a.feet, b.feet);
}
/** Retain unchanged driver identities through clock ticks and refreshed snapshots. */
export function reconcileGeometry(previous: DriverGeometry[], target: DriverGeometry[]): DriverGeometry[] {
  const old = new Map(previous.map(driver => [driver.number, driver]));
  const next = target.map(driver => { const before = old.get(driver.number); return before && sameDriverGeometry(before, driver) ? before : driver; });
  return previous.length === next.length && next.every((driver, index) => driver === previous[index]) ? previous : next;
}
/** Movement can wait for RAF; changed evidence and disappearing nodes cannot. */
export function needsImmediateGeometry(previous: DriverGeometry[], next: DriverGeometry[]): boolean {
  return previous.length !== next.length || next.some((driver, index) => {
    const before = previous[index];
    return before.number !== driver.number || before.view !== driver.view || Boolean(before.node) !== Boolean(driver.node)
      || before.points.length !== driver.points.length || before.feet.length !== driver.feet.length
      || driver.view !== 'chase' && (!samePlotPoints(before.points, driver.points) || !sameFeet(before.feet, driver.feet));
  });
}
/** Data positions are exact; resampling is reserved for explicit view transitions. */
export function geometryTransition(from: DriverGeometry[], to: DriverGeometry[], morph = false): (progress: number) => DriverGeometry[] {
  if (!morph) return () => to;
  const old = new Map(from.map(driver => [driver.number, driver]));
  const plans = to.map(driver => {
    const previous = old.get(driver.number);
    if (!previous || sameDriverGeometry(previous, driver)) return { driver };
    const points = driver.points.map((point, index) => atFraction(previous.points, index / Math.max(1, driver.points.length - 1), point));
    const feet = driver.feet.map((point, index) => atFraction(previous.feet, index / Math.max(1, driver.feet.length - 1), point));
    return { driver, previous, points, feet,
      movePoints: points.some((point, index) => !samePoint(point, driver.points[index])),
      moveFeet: feet.some((point, index) => !samePoint(point, driver.feet[index])),
    };
  });
  return progress => {
    if (progress >= 1) return to;
    const blend = (a: Point, b: Point) => samePoint(a, b) ? b : ({ x: lerp(a.x, b.x, progress), y: lerp(a.y, b.y, progress) });
    return plans.map(({ driver, previous, points, feet, movePoints, moveFeet }) => !previous ? driver : ({ ...driver,
      points: movePoints ? driver.points.map((point, index) => ({ ...point, ...blend(points![index], point) })) : driver.points,
      feet: moveFeet ? driver.feet.map((point, index) => blend(feet![index], point)) : driver.feet,
      node: driver.node ? blend(previous.node ?? driver.node, driver.node) : null, label: blend(previous.label, driver.label),
    }));
  };
}
export function blendGeometry(from: DriverGeometry[], to: DriverGeometry[], progress: number, morph = false): DriverGeometry[] {
  return geometryTransition(from, to, morph)(progress);
}

interface GeometryVelocity { points: Point[]; feet: Point[]; node: Point; label: Point }
interface LiveTail { distance: number; velocity: number; lengths: number[]; total: number }
interface MovingDriver { target: DriverGeometry; current: DriverGeometry; velocity: GeometryVelocity; morphing: boolean; moving: boolean; tail?: LiveTail }
const zero = (): Point => ({ x: 0, y: 0 });
const zeroVelocity = (): GeometryVelocity => ({ points: [], feet: [], node: zero(), label: zero() });

/** Exact critical-damping solution: elapsed time, not frame count, controls movement. */
function springAxis(position: number, velocity: number, target: number, seconds: number): [number, number] {
  const offset = position - target, frequency = 30;
  if (offset === 0 && velocity === 0) return [target, 0];
  const coefficient = velocity + frequency * offset, decay = Math.exp(-frequency * seconds);
  const next = target + (offset + coefficient * seconds) * decay;
  const speed = (velocity - frequency * coefficient * seconds) * decay;
  // A rapidly changing destination can invalidate the original damping envelope.
  // Stop at that destination rather than overshooting a data coordinate.
  if (offset === 0 || offset * (next - target) <= 0 || Math.abs(next - target) < .01 && Math.abs(speed) < .1) return [target, 0];
  return [next, speed];
}
function followPoint(current: Point, target: Point, velocity: Point, seconds: number): Point {
  const [x, vx] = springAxis(current.x, velocity.x, target.x, seconds), [y, vy] = springAxis(current.y, velocity.y, target.y, seconds);
  velocity.x = vx; velocity.y = vy;
  return x === target.x && y === target.y ? target : { x, y };
}
function observationKeys(driver: DriverGeometry): string[] {
  const occurrences = new Map<string, number>();
  return driver.points.map((point, index) => {
    if (driver.view === 'chase') return String(index);
    const key = `${point.lap ?? ''}:${point.atMs}`, occurrence = occurrences.get(key) ?? 0;
    occurrences.set(key, occurrence + 1); return `${key}:${occurrence}`;
  });
}
/** Incoming samples retain the current layout transition and each observation's velocity. */
function alignMovingPath(state: MovingDriver, next: DriverGeometry, morph: boolean) {
  const { current, velocity, target } = state;
  const indices = new Map(observationKeys(target).map((key, index) => [key, index]));
  const keys = observationKeys(next);
  const before = (points: Point[], index: number, fallback: Point) => morph
    ? atFraction(points, index / Math.max(1, next.points.length - 1), fallback)
    : points[indices.get(keys[index]) ?? -1] ?? fallback;
  return {
    points: next.points.map((point, index) => ({ ...point, ...before(current.points, index, point) })),
    feet: next.feet.map((point, index) => before(current.feet, index, point)),
    velocityPoints: next.points.map((_, index) => ({ ...before(velocity.points, index, zero()) })),
    velocityFeet: next.feet.map((_, index) => ({ ...before(velocity.feet, index, zero()) })),
  };
}

function liveGeometry(driver: DriverGeometry): DriverGeometry {
  const end = driver.points.at(-1);
  if (driver.view === 'chase' || !driver.node || !end) return driver;
  return samePoint(driver.node, end) && driver.headAtMs === end.atMs ? driver
    : { ...driver, node: { x: end.x, y: end.y }, headAtMs: end.atMs };
}
function pathLengths(points: PlotPoint[]): number[] {
  let distance = 0;
  return points.map((point, index) => {
    if (index && !point.breakBefore) distance += Math.hypot(point.x - points[index - 1].x, point.y - points[index - 1].y);
    return distance;
  });
}
/** A growing tail may extend a held rank, but it cannot rewrite an observed prefix. */
function canGrowPath(previous: DriverGeometry, next: DriverGeometry): boolean {
  if (previous.view !== next.view || !previous.node || !next.node || !previous.points.length || !next.points.length) return false;
  const held = previous.points.at(-1)!.hold === true;
  const fixed = previous.points.length - (held ? 1 : 0);
  if (next.points.length < previous.points.length || next.feet.length !== next.points.length) return false;
  for (let index = 0; index < fixed; index++) {
    if (!samePlotPoints([previous.points[index]], [next.points[index]]) || !samePoint(previous.feet[index], next.feet[index])) return false;
  }
  if (next.points.slice(fixed).some(point => point.breakBefore)) return false;
  const last = previous.points.at(-1)!;
  const firstAdded = next.points[fixed];
  if (firstAdded && firstAdded.atMs < last.atMs) return false;
  // The old horizontal endcap must remain on the new path. A late rank change
  // inside that interval is a correction, so publish its actual geometry at once.
  return !held || Boolean(firstAdded && firstAdded.x >= last.x && firstAdded.y === last.y);
}
/** Build only the visible prefix, including at most one uncommitted drawing tip. */
function revealedGeometry(target: DriverGeometry, tail: LiveTail): DriverGeometry {
  if (tail.distance >= tail.total) return target;
  let index = 0;
  while (index + 1 < tail.lengths.length && tail.lengths[index + 1] <= tail.distance) index++;
  const points = target.points.slice(0, index + 1), feet = target.feet.slice(0, index + 1);
  const from = target.points[index], to = target.points[index + 1];
  let node: Point = { x: from.x, y: from.y };
  if (to && tail.distance > tail.lengths[index]) {
    const fraction = (tail.distance - tail.lengths[index]) / (tail.lengths[index + 1] - tail.lengths[index]);
    node = { x: lerp(from.x, to.x, fraction), y: lerp(from.y, to.y, fraction) };
    points.push({ ...to, ...node, partial: true });
    const a = target.feet[index], b = target.feet[index + 1];
    feet.push({ x: lerp(a.x, b.x, fraction), y: lerp(a.y, b.y, fraction) });
  }
  return { ...target, points, feet, node, headAtMs: from.atMs,
    label: target.view === 'story' ? { x: node.x + 7, y: node.y } : target.label };
}
const visibleGeometry = (state: MovingDriver) => state.tail ? revealedGeometry(state.current, state.tail) : state.current;

/** Presentation follows moving targets; updating a target never resets position or speed. */
export class GeometryMotion {
  current: DriverGeometry[];
  private target: DriverGeometry[];
  private drivers = new Map<number, MovingDriver>();
  private sampledAt: number | null = null;
  private mode: StudioMotionMode;
  constructor(initial: DriverGeometry[], mode: StudioMotionMode = 'replay') {
    this.mode = mode;
    this.current = this.target = mode === 'live' ? initial.map(liveGeometry) : initial;
    this.resetDrivers(this.target);
  }
  private resetDrivers(target: DriverGeometry[]) {
    this.drivers = new Map(target.map(driver => [driver.number, { current: driver, target: driver, velocity: zeroVelocity(), morphing: false, moving: false }]));
  }
  get active() { return [...this.drivers.values()].some(driver => driver.moving); }
  retarget(target: DriverGeometry[], now: number, morph = false, direct = false, mode: StudioMotionMode = 'replay'): boolean {
    direct ||= this.mode !== mode;
    this.mode = mode;
    const next = reconcileGeometry(this.target, mode === 'live' ? target.map(liveGeometry) : target);
    if (next === this.target && !morph && !direct) return false;
    if (direct) {
      const changed = this.current !== next;
      this.target = next; this.current = next; this.sampledAt = now; this.resetDrivers(next); return changed;
    }
    this.sample(now);
    const updated = new Map<number, MovingDriver>();
    for (const driver of next) {
      const state = this.drivers.get(driver.number);
      if (!state) { updated.set(driver.number, { target: driver, current: driver, velocity: zeroVelocity(), morphing: false, moving: false }); continue; }
      updated.set(driver.number, state);
      if (!morph && driver === state.target) continue;
      if (mode === 'live' && driver.view !== 'chase' && !morph) {
        if (canGrowPath(state.target, driver)) {
          const lengths = pathLengths(driver.points), total = lengths.at(-1) ?? 0;
          const tail: LiveTail = { lengths, total, distance: state.tail?.distance ?? pathLengths(state.target.points).at(-1) ?? 0, velocity: state.tail?.velocity ?? 0 };
          state.tail = tail;
          if (!state.morphing) {
            state.target = state.current = driver; state.moving = tail.distance < total;
            state.velocity = zeroVelocity();
            continue;
          }
          // While a view transition is running, retain its projected positions
          // and separately reveal only the new tail. Both share the same RAF.
        } else {
          // Gaps, removed observations, corrections and returning nodes take
          // effect immediately; none is a new continuously observed segment.
          state.target = state.current = driver; state.tail = undefined;
          state.velocity = zeroVelocity(); state.moving = state.morphing = false;
          continue;
        }
      }
      if (morph || mode !== 'live' || driver.view === 'chase') {
        state.current = visibleGeometry(state);
        state.tail = undefined;
      }
      const current = state.current, velocity = state.velocity;
      const movePath = morph || state.morphing || driver.view === 'chase';
      let points = driver.points, feet = driver.feet, label = driver.label;
      if (movePath) {
        if (morph || driver.points !== state.target.points || driver.feet !== state.target.feet) {
          const aligned = alignMovingPath(state, driver, morph);
          points = aligned.points; feet = aligned.feet;
          velocity.points = aligned.velocityPoints; velocity.feet = aligned.velocityFeet;
        } else { points = current.points; feet = current.feet; }
        label = current.label;
      } else { velocity.points = []; velocity.feet = []; velocity.label = zero(); }
      const node = driver.node && current.node ? current.node : driver.node;
      if (!driver.node || !current.node) velocity.node = zero();
      state.target = driver;
      state.current = { ...driver, points, feet, label, node };
      state.morphing = morph || state.morphing;
      state.moving = !sameDriverGeometry(state.current, driver) || Boolean(state.tail && state.tail.distance < state.tail.total);
      if (!state.moving) { state.current = driver; state.velocity = zeroVelocity(); state.morphing = false; }
    }
    this.drivers = updated; this.target = next;
    this.current = next.map(driver => visibleGeometry(updated.get(driver.number)!));
    return true;
  }
  sample(now: number): DriverGeometry[] {
    // RAF's frame timestamp may precede a layout-effect sample in the same frame.
    const sampledAt = Math.max(now, this.sampledAt ?? now);
    const seconds = this.sampledAt === null ? 0 : (sampledAt - this.sampledAt) / 1000;
    this.sampledAt = sampledAt;
    if (!seconds || !this.active) return this.current;
    this.current = this.target.map(target => {
      const state = this.drivers.get(target.number)!;
      if (!state.moving) return target;
      if (state.tail && !state.morphing) {
        const tail = state.tail;
        [tail.distance, tail.velocity] = springAxis(tail.distance, tail.velocity, tail.total, seconds);
        state.current = target;
        state.moving = tail.distance < tail.total;
        if (!state.moving) state.tail = undefined;
        return visibleGeometry(state);
      }
      const { current, velocity } = state;
      let points = target.points, feet = target.feet, label = target.label;
      if (state.morphing || target.view === 'chase') {
        points = target.points.map((point, index) => {
          const projected = followPoint(current.points[index] ?? point, point, velocity.points[index] ??= zero(), seconds);
          return projected === point ? point : { ...point, ...projected };
        });
        feet = target.feet.map((point, index) => followPoint(current.feet[index] ?? point, point, velocity.feet[index] ??= zero(), seconds));
        if (points.every((point, index) => point === target.points[index])) points = target.points;
        if (feet.every((point, index) => point === target.feet[index])) feet = target.feet;
        label = followPoint(current.label, target.label, velocity.label, seconds);
        if (points === target.points && feet === target.feet && label === target.label) state.morphing = false;
      }
      const tip = points.at(-1);
      const node = target.node ? this.mode === 'live' && target.view !== 'chase' && tip
        ? { x: tip.x, y: tip.y }
        : followPoint(current.node ?? target.node, target.node, velocity.node, seconds) : null;
      state.current = { ...target, points, feet, label, node };
      if (state.tail) {
        [state.tail.distance, state.tail.velocity] = springAxis(state.tail.distance, state.tail.velocity, state.tail.total, seconds);
        if (state.tail.distance >= state.tail.total) state.tail = undefined;
      }
      state.moving = !sameDriverGeometry(state.current, target) || Boolean(state.tail);
      if (!state.moving) { state.current = target; state.velocity = zeroVelocity(); state.morphing = false; }
      return visibleGeometry(state);
    });
    return this.current;
  }
}
